import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner, Modal, Form, Button } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles, InstructorModalStyles } from './styles/adminInstructors.js';
import {
    ADMIN_INSTRUCTORS_ENDPOINT,
    ADMIN_INSTRUCTORS_UPDATE_ENDPOINT
} from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
];

const initialForm = {
    name: '',
    slug: '',
    title: '',
    bio: '',
    email: '',
    phone: '',
    profileImage: '',
    expertise: '',
    status: 'active'
};

const AdminInstructors = () => {
    const { user, loading: authLoading } = useAuth();
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalItems: 0,
        totalPages: 1
    });
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [modalState, setModalState] = useState({
        show: false,
        mode: 'add',
        instructor: null,
        form: initialForm,
        saving: false,
        error: ''
    });
    const [deletingId, setDeletingId] = useState('');

    const isAdmin = useMemo(() => (
        Array.isArray(user?.roles)
        && user.roles.some((role) => typeof role === 'string' && role.toLowerCase() === 'admin')
    ), [user]);

    const fetchInstructors = useCallback(async () => {
        if (!isAdmin) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(pageSize)
            });
            if (searchTerm.trim()) {
                params.set('search', searchTerm.trim());
            }
            if (statusFilter) {
                params.set('status', statusFilter);
            }

            const response = await fetch(`${ADMIN_INSTRUCTORS_ENDPOINT}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load instructors.');
            }

            const payload = await response.json();
            setInstructors(payload.instructors || []);
            if (payload.pagination) {
                const serverPage = payload.pagination.page || page;
                setPagination({
                    page: serverPage,
                    limit: payload.pagination.limit || pageSize,
                    totalItems: payload.pagination.totalItems || 0,
                    totalPages: payload.pagination.totalPages || 1
                });
                if (serverPage !== page) {
                    setPage(serverPage);
                }
            } else {
                setPagination({
                    page,
                    limit: pageSize,
                    totalItems: payload.instructors?.length || 0,
                    totalPages: 1
                });
            }
        } catch (err) {
            setError(err.message || 'Unable to load instructors.');
            setInstructors([]);
        } finally {
            setLoading(false);
        }
    }, [isAdmin, page, pageSize, searchTerm, statusFilter]);

    useEffect(() => {
        if (!authLoading && isAdmin) {
            fetchInstructors();
        }
    }, [authLoading, isAdmin, fetchInstructors]);

    const tableRows = useMemo(() => instructors.map((instructor) => ({
        id: instructor._id,
        name: instructor.name || 'Instructor',
        slug: instructor.slug,
        title: instructor.title || '—',
        email: instructor.email || '—',
        status: instructor.status ? instructor.status.charAt(0).toUpperCase() + instructor.status.slice(1) : 'Active',
        expertise: Array.isArray(instructor.expertise) ? instructor.expertise : [],
        raw: instructor
    })), [instructors]);

    const openModal = (mode, instructor = null) => {
        if (mode === 'edit' && instructor) {
            setModalState({
                show: true,
                mode,
                instructor,
                form: {
                    name: instructor.name || '',
                    slug: instructor.slug || '',
                    title: instructor.title || '',
                    bio: instructor.bio || '',
                    email: instructor.email || '',
                    phone: instructor.phone || '',
                    profileImage: instructor.profileImage || '',
                    expertise: Array.isArray(instructor.expertise) ? instructor.expertise.join(', ') : '',
                    status: instructor.status || 'active'
                },
                saving: false,
                error: ''
            });
        } else {
            setModalState({
                show: true,
                mode: 'add',
                instructor: null,
                form: initialForm,
                saving: false,
                error: ''
            });
        }
    };

    const closeModal = () => {
        setModalState({
            show: false,
            mode: 'add',
            instructor: null,
            form: initialForm,
            saving: false,
            error: ''
        });
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setModalState((prev) => ({
            ...prev,
            form: {
                ...prev.form,
                [name]: value
            },
            error: ''
        }));
    };

    const submitModal = async (event) => {
        event.preventDefault();
        setModalState((prev) => ({ ...prev, saving: true, error: '' }));

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            if (!modalState.form.name.trim()) {
                throw new Error('Name is required.');
            }

            const payload = {
                name: modalState.form.name.trim(),
                slug: modalState.form.slug.trim(),
                title: modalState.form.title.trim(),
                bio: modalState.form.bio.trim(),
                email: modalState.form.email.trim(),
                phone: modalState.form.phone.trim(),
                profileImage: modalState.form.profileImage.trim(),
                status: modalState.form.status,
                expertise: modalState.form.expertise
                    ? modalState.form.expertise.split(',').map((item) => item.trim()).filter(Boolean)
                    : []
            };

            const requestOptions = {
                method: modalState.mode === 'add' ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            };

            const endpoint = modalState.mode === 'add'
                ? ADMIN_INSTRUCTORS_ENDPOINT
                : ADMIN_INSTRUCTORS_UPDATE_ENDPOINT(modalState.instructor._id);

            const response = await fetch(endpoint, requestOptions);

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.message || 'Unable to save instructor.');
            }

            await response.json();
            closeModal();
            fetchInstructors();
        } catch (err) {
            setModalState((prev) => ({ ...prev, error: err.message || 'Unable to save instructor.' }));
        } finally {
            setModalState((prev) => ({ ...prev, saving: false }));
        }
    };

    const deleteInstructor = async (id) => {
        if (!window.confirm('Are you sure you want to delete this instructor?')) {
            return;
        }
        setDeletingId(id);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_INSTRUCTORS_UPDATE_ENDPOINT(id), {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to delete instructor.');
            }

            fetchInstructors();
        } catch (err) {
            setError(err.message || 'Unable to delete instructor.');
        } finally {
            setDeletingId('');
        }
    };

    return (
        <Styles>
            <InstructorModalStyles />
            <div className="main-wrapper admin-instructors-page">
                <HeaderTwo />
                <BreadcrumbBox title="Manage Instructors" />

                <section className="instructors-area">
                    <Container>
                        <Row>
                            <Col md="12">
                                {!authLoading && !isAdmin && (
                                    <Alert variant="danger">You do not have permission to view this page.</Alert>
                                )}
                                {isAdmin && (
                                    <div className="card-box">
                                        <div className="actions">
                                            <Button onClick={() => openModal('add')}>Add Instructor</Button>
                                            <div className="filters">
                                                <input
                                                    type="text"
                                                    placeholder="Search instructors..."
                                                    value={searchInput}
                                                    onChange={(event) => setSearchInput(event.target.value)}
                                                    disabled={loading}
                                                />
                                                <select
                                                    value={statusFilter}
                                                    onChange={(event) => {
                                                        setStatusFilter(event.target.value);
                                                        setPage(1);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <option value="">All Statuses</option>
                                                    {STATUS_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => { setSearchInput(''); setSearchTerm(''); setStatusFilter(''); setPage(1); }}
                                                    disabled={loading}
                                                >
                                                    Reset
                                                </Button>
                                                <Button
                                                    variant="success"
                                                    onClick={() => { setSearchTerm(searchInput.trim()); setPage(1); }}
                                                    disabled={loading}
                                                >
                                                    Apply
                                                </Button>
                                            </div>
                                        </div>
                                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                                        {loading ? (
                                            <div className="text-center my-4">
                                                <Spinner animation="border" role="status" />
                                            </div>
                                        ) : (
                                            <div className="data-grid">
                                                {tableRows.length === 0 ? (
                                                    <Alert variant="info" className="mb-0">No instructors found.</Alert>
                                                ) : (
                                                    <>
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Name</th>
                                                                    <th>Title</th>
                                                                    <th>Email</th>
                                                                    <th>Expertise</th>
                                                                    <th>Status</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tableRows.map((row) => (
                                                                    <tr key={row.id}>
                                                                        <td>
                                                                            <div className="blog-title-cell">
                                                                                <strong>{row.name}</strong>
                                                                                <span className="slug">{row.slug}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td>{row.title}</td>
                                                                        <td>{row.email}</td>
                                                                        <td>
                                                                            {row.expertise.length > 0 ? row.expertise.map((item) => (
                                                                                <span className="expertise-chip" key={`${row.id}-${item}`}>{item}</span>
                                                                            )) : <span className="muted">N/A</span>}
                                                                        </td>
                                                                        <td className="text-capitalize">{row.status}</td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="link-button"
                                                                                onClick={() => openModal('edit', row.raw)}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                            <span> | </span>
                                                                            <button
                                                                                type="button"
                                                                                className="link-button"
                                                                                onClick={() => deleteInstructor(row.id)}
                                                                                disabled={deletingId === row.id}
                                                                            >
                                                                                {deletingId === row.id ? 'Deleting...' : 'Delete'}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <div className="pagination-controls">
                                                            <div className="page-info">
                                                                Page {pagination.page} of {pagination.totalPages} · {pagination.totalItems} total
                                                            </div>
                                                            <div className="page-actions">
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                                                    disabled={pagination.page <= 1 || loading}
                                                                >
                                                                    Previous
                                                                </Button>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                                                                    disabled={pagination.page >= pagination.totalPages || loading}
                                                                >
                                                                    Next
                                                                </Button>
                                                                <select
                                                                    value={pageSize}
                                                                    onChange={(event) => {
                                                                        const newSize = Number(event.target.value) || 10;
                                                                        setPageSize(newSize);
                                                                        setPage(1);
                                                                    }}
                                                                    disabled={loading}
                                                                >
                                                                    {[10, 20, 50, 100].map((size) => (
                                                                        <option key={size} value={size}>
                                                                            {size} / page
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>

            <Modal
                show={modalState.show}
                onHide={closeModal}
                centered
                dialogClassName="admin-instructors-modal"
                size="lg"
            >
                <Form onSubmit={submitModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{modalState.mode === 'add' ? 'Add Instructor' : 'Edit Instructor'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalState.error && <Alert variant="danger">{modalState.error}</Alert>}
                        <Row>
                            <Col md="6">
                                <Form.Group controlId="instructor_name">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={modalState.form.name}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="instructor_slug">
                                    <Form.Label>Slug</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="slug"
                                        value={modalState.form.slug}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="leave blank to auto-generate"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="6">
                                <Form.Group controlId="instructor_title">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={modalState.form.title}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Senior Lecturer"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="instructor_status">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={modalState.form.status}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="6">
                                <Form.Group controlId="instructor_email">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={modalState.form.email}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="name@example.com"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="instructor_phone">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={modalState.form.phone}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="+1 555 123 4567"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="instructor_profileImage">
                                    <Form.Label>Profile Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="profileImage"
                                        value={modalState.form.profileImage}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="https://example.com/photo.jpg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="instructor_expertise">
                                    <Form.Label>Expertise (comma separated)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="expertise"
                                        value={modalState.form.expertise}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Mathematics, Data Science"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="instructor_bio">
                                    <Form.Label>Bio</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="bio"
                                        value={modalState.form.bio}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Instructor biography"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={closeModal} disabled={modalState.saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={modalState.saving}>
                            {modalState.saving ? 'Saving...' : 'Save Instructor'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Styles>
    );
};

export default AdminInstructors;
